import type { ContractStaffing } from './contractStaffing';

/**
 * The task panel's contract hours bar (3 Nov, Mette): the contract's Own Work
 * hours as ONE bar, read the way the task's hours bar at the top of the panel
 * is (`hoursBarSegments.ts`), so the panel's two sections read alike — this
 * task's hours, this contract's hours. It replaced the three-bar cascade
 * (contract → task budgets → allocated → planned), which showed allocation a
 * second time beside the Allocated box and asked the reader to compare bar
 * widths.
 *
 * Pure segment math, no React. Segments partition the drawn bar:
 *   allocated   — hours people are allocated to on the contract's tasks
 *   unallocated — task budget hours nobody is allocated to yet (hatched)
 *   notOnTasks  — contract hours no task budgets yet (dashed outline)
 *   over        — task budgets or allocations beyond the contract (coral) —
 *                 ADDITIVE, never a clamp: the bar grows to contract + over.
 *
 * Planned hours have no segment: the prototype plans allocations as totals,
 * not over days (ADR 0021), so there is nothing to draw yet.
 */
export type ContractHoursSegmentKind = 'allocated' | 'unallocated' | 'notOnTasks' | 'over';

export type ContractHoursSegment = { kind: ContractHoursSegmentKind; hours: number; pct: number };

export type ContractHoursBarModel = {
  /** Partition of the drawn bar; zero-hour segments omitted. */
  segments: ContractHoursSegment[];
  /** The bar's 100%: the contract's hours + over. */
  totalHours: number;
  /** Allocated always (as the task bar's legend); the rest only when they hold hours. */
  legend: ContractHoursSegment[];
};

/** Null when every input is 0 — nothing to measure, so no empty bar. */
export const buildContractHoursBar = (
  staffing: Pick<ContractStaffing, 'budgetHours' | 'distributedHours' | 'allocatedHours'>,
): ContractHoursBarModel | null => {
  const contract = Math.max(0, staffing.budgetHours);
  const tasks = Math.max(0, staffing.distributedHours);
  const allocated = Math.max(0, staffing.allocatedHours);
  if (contract === 0 && tasks === 0 && allocated === 0) return null;

  // Whichever reaches further — the task budgets or the allocations — is what
  // the contract is measured against; over is what exceeds it.
  const reach = Math.max(tasks, allocated);
  const over = Math.max(0, reach - contract);
  const total = contract + over;

  const parts: { kind: ContractHoursSegmentKind; hours: number }[] = [
    { kind: 'allocated', hours: Math.min(allocated, contract) },
    { kind: 'unallocated', hours: Math.max(0, Math.min(tasks, contract) - allocated) },
    { kind: 'notOnTasks', hours: Math.max(0, contract - reach) },
    { kind: 'over', hours: over },
  ];
  const withPct = (part: { kind: ContractHoursSegmentKind; hours: number }) => ({
    ...part,
    pct: (part.hours / total) * 100,
  });

  return {
    segments: parts.filter((part) => part.hours > 0).map(withPct),
    totalHours: total,
    legend: parts.filter((part) => part.kind === 'allocated' || part.hours > 0).map(withPct),
  };
};
